// AI Service for handling audio and image generation
// This service provides a centralized way to handle AI-powered content generation
// Connected to GraphQL endpoints used by mobile app

import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { 
  GENERATE_IMAGE, 
  GENERATE_CATEGORY_IMAGE, 
  GENERATE_AUDIO, 
  GENERATE_CATEGORY_AUDIO,
  GENERATE_AUDIO_ALL_VOICES
} from '../graphql/mutations';

interface AudioGenerationOptions {
  text: string;
  voice?: string;
  speed?: number;
  language?: string;
}

interface ImageGenerationOptions {
  prompt: string;
  style?: string;
  size?: string;
  quality?: string;
}

// GraphQL Input Types (matching the server schema)
interface AudioInput {
  text: string;
  voiceId?: string;
}

interface ImageInput {
  prompt: string;
  size?: string; // "1024x1024", "1792x1024", "1024x1792"
  quality?: string; // "standard" or "hd"
  returnBase64: boolean;
}

interface GeneratedAudio {
  audioData: string; // base64 encoded
  fileName: string;
  contentType: string;
  duration?: number;
}

interface GeneratedImage {
  imageData: string; // base64 encoded
  fileName: string;
  contentType: string;
  width?: number;
  height?: number;
}

// GraphQL query to get all voices
const GET_VOICES = gql`
  query GetVoices {
    voices {
      id
      voiceId
      name
      description
    }
  }
`;

class AIService {
  
  /**
   * Generate audio from text using GraphQL endpoint (same as mobile app)
   * @param options Audio generation options
   * @param targetCode Code of the item or category to generate audio for
   * @param targetType Type of target ('item' or 'category')
   * @returns Promise with generation result
   */
  async generateAudio(options: AudioGenerationOptions, targetCode?: string, targetType: 'item' | 'category' = 'item'): Promise<{success: boolean, message: string, audioUrl?: string}> {
    // Validate the text first
    const validation = this.validateAudioText(options.text);
    if (!validation.isValid) {
      return {
        success: false,
        message: validation.message || 'Invalid audio text'
      };
    }

    if (!targetCode) {
      return {
        success: false,
        message: 'Target code is required for audio generation'
      };
    }

    // Fetch voices
    const voices = await this.getAvailableVoices();
    let voiceId: string;
    if (options.voice && voices.some(v => v.voiceId === options.voice)) {
      // Use provided voice if valid
      voiceId = options.voice;
    } else if (voices.length > 0) {
      // Fallback to first available voice
      voiceId = voices[0].voiceId;
      console.log(`Selected default voice ID: ${voiceId}`);
    } else {
      console.error('No available voices for audio generation');
      return {
        success: false,
        message: 'No available voices for audio generation'
      };
    }

    try {
      console.log(`Generating audio for ${targetType}:`, targetCode);
      
      // Step 1: Generate the audio
      const audioInput: AudioInput = {
        text: options.text,
        voiceId: voiceId
      };
      
      const result = await apolloClient.mutate({
        mutation: GENERATE_AUDIO,
        variables: { 
          input: audioInput
        }
      });
      
      if (!result.data?.generateAudio?.audioBase64) {
        return {
          success: false,
          message: 'No audio data returned from generation'
        };
      }
      
      return {
        success: true,
        message: 'Audio generated successfully',
        audioUrl: `data:audio/mpeg;base64,${result.data.generateAudio.audioBase64}`
      };
    } catch (error: any) {
      console.error('Error generating audio:', error);
      return {
        success: false,
        message: `Failed to generate audio: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Generate image from text prompt using GraphQL endpoint (same as mobile app)
   * @param options Image generation options
   * @param targetCode Code of the item or category to generate image for
   * @param targetType Type of target ('item' or 'category')
   * @returns Promise with generation result
   */
  async generateImage(options: ImageGenerationOptions, targetCode?: string, targetType: 'item' | 'category' = 'item'): Promise<{success: boolean, message: string, imageUrl?: string}> {
    // Validate the prompt first
    const validation = this.validateImagePrompt(options.prompt);
    if (!validation.isValid) {
      return {
        success: false,
        message: validation.message || 'Invalid image prompt'
      };
    }

    if (!targetCode) {
      return {
        success: false,
        message: 'Target code is required for image generation'
      };
    }

    const size = options.size || '1024x1024';
    
    try {
      console.log(`Generating image for ${targetType}:`, targetCode, options.prompt);
      
      // Step 1: Generate the image
      const imageInput: ImageInput = {
        prompt: options.prompt,
        size: size,
        quality: options.quality || 'standard',
        returnBase64: true
      };
      
      const result = await apolloClient.mutate({
        mutation: GENERATE_IMAGE,
        variables: { 
          input: imageInput
        }
      });
      
      if (!result.data?.generateImage?.imageBase64) {
        return {
          success: false,
          message: 'No image data returned from generation'
        };
      }
      
      return {
        success: true,
        message: 'Image generated successfully',
        imageUrl: `data:image/png;base64,${result.data.generateImage.imageBase64}`
      };
    } catch (error: any) {
      console.error('Error generating image:', error);
      return {
        success: false,
        message: `Failed to generate image: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Convert blob to base64 string
   * @param blob Blob to convert
   * @returns Promise with base64 string
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get available voices for text-to-speech
   * @returns Promise with list of available ElevenLabs voices
   */
  async getAvailableVoices(): Promise<Array<{id: string; voiceId: string; name: string; description?: string}>> {
    const result = await apolloClient.query<{ voices: Array<{ id: string; voiceId: string; name: string; description?: string }> }>({
      query: GET_VOICES,
      fetchPolicy: 'network-only'
    });
    return result.data.voices;
  }

  /**
   * Validate if text is suitable for audio generation
   * @param text Text to validate
   * @returns Validation result
   */
  validateAudioText(text: string): { isValid: boolean; message?: string } {
    if (!text.trim()) {
      return { isValid: false, message: 'Text cannot be empty' };
    }
    
    if (text.length > 3000) {
      return { isValid: false, message: 'Text is too long (maximum 3000 characters)' };
    }
    
    // Check for potentially problematic characters
    const problematicChars = /[<>{}]/g;
    if (problematicChars.test(text)) {
      return { isValid: false, message: 'Text contains unsupported characters' };
    }
    
    return { isValid: true };
  }

  /**
   * Generate audio for ALL system voices in one atomic server-side operation.
   * If there are insufficient ElevenLabs credits for every voice, no audio is
   * generated and the returned array is empty (the error message is in `errorMessage`).
   */
  async generateAudioAllVoices(text: string): Promise<{
    results: Array<{ voiceId: string; voiceName: string; audioBase64: string }>;
    errorMessage?: string;
  }> {
    try {
      const result = await apolloClient.mutate<{
        generateAudioAllVoices: Array<{ voiceId: string; voiceName: string; audioBase64: string }>;
      }>({
        mutation: GENERATE_AUDIO_ALL_VOICES,
        variables: { text }
      });
      return { results: result.data?.generateAudioAllVoices ?? [] };
    } catch (error: any) {
      // Surface the server's credit-error message verbatim so the UI can display it
      const message: string =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Failed to generate audio';
      return { results: [], errorMessage: message };
    }
  }

  /**
   * Validate if prompt is suitable for image generation
   * @param prompt Prompt to validate
   * @returns Validation result
   */
  validateImagePrompt(prompt: string): { isValid: boolean; message?: string } {
    if (!prompt.trim()) {
      return { isValid: false, message: 'Prompt cannot be empty' };
    }
    
    if (prompt.length > 1000) {
      return { isValid: false, message: 'Prompt is too long (maximum 1000 characters)' };
    }
    
    // Check for potentially inappropriate content (basic check)
    const inappropriateTerms = ['violence', 'hate', 'explicit'];
    const lowerPrompt = prompt.toLowerCase();
    for (const term of inappropriateTerms) {
      if (lowerPrompt.includes(term)) {
        return { isValid: false, message: 'Prompt contains inappropriate content' };
      }
    }
    
    return { isValid: true };
  }
}

export const aiService = new AIService();
export default aiService;
